import { useId } from 'react';
import { cn } from '../../../lib/utils';

export type MatchQuizProgressProps = {
  currentStep: number;
  totalSteps: number;
  className?: string;
};

/**
 * `currentStep` is 1-based for display (e.g. 3 of 6). Progress fill uses the same ratio as the label.
 */
export function MatchQuizProgress({
  currentStep,
  totalSteps,
  className,
}: MatchQuizProgressProps) {
  const labelId = useId();
  const safeTotal = Math.max(1, totalSteps);
  const clamped = Math.min(Math.max(1, currentStep), safeTotal);
  const valueNow = clamped;
  const valueMax = safeTotal;
  const percent = Math.round((clamped / safeTotal) * 100);

  return (
    <div className={cn('w-full', className)}>
      <div className='flex items-center justify-between gap-3 text-sm text-muted-foreground'>
        <p id={labelId} className='font-medium text-foreground'>
          Step {clamped} of {safeTotal}
        </p>
        <span className='tabular-nums' aria-hidden>
          {percent}%
        </span>
      </div>
      <div
        className='mt-2 h-2 w-full overflow-hidden rounded-full bg-muted'
        role='progressbar'
        aria-valuemin={1}
        aria-valuemax={valueMax}
        aria-valuenow={valueNow}
        aria-valuetext={`Step ${clamped} of ${safeTotal}, ${percent} percent`}
        aria-labelledby={labelId}
      >
        <div
          className='h-full rounded-full bg-primary-600 transition-[width] duration-300 ease-out'
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
